import { memo, useMemo } from "react";

import { TableActions } from "@sso/components/TableActions";
import { TableThWithSortIcon } from "@sso/components/TableThWithSortIcon";
import { Dialog, UserRole } from "@sso/constant";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { openDialog } from "@sso/store/slices/dialog";
import { setLicenceSortColumn } from "@sso/store/slices/licence";
import { ID, LicenceSummary, LicenceSummaryKeys, LicenceType, TableColumn } from "@sso/types";
import { LayoutTable, TD, TH, TableWrapper } from "@sso/ui/Table";

interface LicencesTableProps {
  data: LicenceSummary[];
  licenceTableColumns: TableColumn<string>[];
  dataKeys: LicenceSummaryKeys[];
}

export const LicencesTable = memo(({ data, licenceTableColumns, dataKeys }: LicencesTableProps) => {
  const user = useAppSelector(selectCurrentUser);
  const isAdmin = user?.role.roleId === UserRole.ADMINISTRATOR;
  const dispatch = useAppDispatch();
  const handleSortAction = (column: string) => {
    dispatch(setLicenceSortColumn(column));
  };

  const mapType = (licence: LicenceSummary): string => {
    switch (licence[LicenceSummaryKeys.TYPE]) {
      case LicenceType.SINGLE_USER:
        return "Single User";
      case LicenceType.CONCURRENT:
        return "Concurrent";
      case LicenceType.TRIAL:
        return "Trial";
      case LicenceType.API:
        return "API";
    }
  };

const data2: LicenceSummary[] = useMemo(
  () =>
    data.map(licence => {
      const updateDateValue = licence[LicenceSummaryKeys.UPDATE_DATE];
      const updateDate = updateDateValue
        ? (() => {
            const d = new Date(updateDateValue);
            return d.getFullYear() === 1 ? "-" : d.toLocaleDateString();
          })()
        : "-";

      return {
        ...licence,
        [LicenceSummaryKeys.TYPE_NAME]: mapType(licence),

        [LicenceSummaryKeys.EXPIRY_DATE_LABEL]: licence[LicenceSummaryKeys.EXPIRY_DATE]
          ? new Date(licence[LicenceSummaryKeys.EXPIRY_DATE]).toLocaleDateString()
          : "-",

        [LicenceSummaryKeys.UPDATE_DATE_LABEL]: updateDate,

        [LicenceSummaryKeys.CREATE_DATE_LABEL]: licence[LicenceSummaryKeys.CREATE_DATE]
          ? new Date(licence[LicenceSummaryKeys.CREATE_DATE]).toLocaleDateString()
          : "-",
      };
    }),
  [data],
);




  const onEditClick = (id: ID) => {
    dispatch(openDialog({ type: Dialog.EDIT_LICENCE, id }));
  };

  const onRemoveClick = (id: ID) => {
    dispatch(openDialog({ type: Dialog.DELETE_LICENCE, id }));
  };

  const onInfoClick = (id: ID) => {
    dispatch(openDialog({ type: Dialog.INFO_LICENCE, id }));
  };

  return (
    <TableWrapper>
      <LayoutTable>
        <thead>
          <tr>
            {licenceTableColumns.map(col => (
              <TH key={col.field} sortable={col.sortable} onClick={() => col.sortable && handleSortAction(col.field)}>
                {col.sortable ? <TableThWithSortIcon column={col.field} title={col.name} /> : col.name}
              </TH>
            ))}
            {isAdmin && <TH textAlign="center">Actions</TH>}
          </tr>
        </thead>
        <tbody>
         {data2.map(licence => (
  <tr key={licence.licenceId}>
    {dataKeys.map(key => {
      const isExpiryDate = key === LicenceSummaryKeys.EXPIRY_DATE_LABEL;

      let isExpired = false;
      if (isExpiryDate && licence[LicenceSummaryKeys.EXPIRY_DATE]) {
        const expiryDate = new Date(licence[LicenceSummaryKeys.EXPIRY_DATE]);
        isExpired = expiryDate < new Date(); // compare with now
      }

      return (
        <TD
          key={key}
          style={{
            color: isExpired ? 'red' : 'inherit', // make text red if expired
            textAlign: licence[key] === '-' ? 'center' : 'left',
          }}
        >
          {licence[key]}
        </TD>
      );
    })}
    {isAdmin && (
      <TD>
        <TableActions
          id={licence.licenceId}
          onEditClick={onEditClick}
          onRemoveClick={onRemoveClick}
          showInfo={licence[LicenceSummaryKeys.ADDITIONAL_DATA] !== null}
          onInfoClick={onInfoClick}
        />
      </TD>
    )}
  </tr>
))}
        </tbody>
      </LayoutTable>
    </TableWrapper>
  );
});
